import { NavLink, Outlet, Link } from "react-router-dom";
import {
  BarChart3,
  CalendarRange,
  HardHat,
  LayoutDashboard,
  RotateCcw,
  Tractor,
} from "lucide-react";
import { APP_NAME } from "@crewledger/shared/constants";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { DemoProvider, useDemo } from "./demo-context";
import { DEMO_COMPANY_NAME } from "./demo-data";

const DEMO_NAV = [
  { to: "/demo/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/demo/daily-entry", label: "Daily Entry", icon: CalendarRange },
  { to: "/demo/equipment", label: "Equipment", icon: Tractor },
  { to: "/demo/pay-period-review", label: "Pay Period Review", icon: HardHat },
  { to: "/demo/reports", label: "Reports", icon: BarChart3 },
] as const;

function DemoBanner() {
  const { reset } = useDemo();

  return (
    <div className="border-b border-amber-200 bg-amber-50 px-4 py-2.5">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-medium text-amber-950">
          Demo Mode — sample construction data.
          <span className="ml-2 font-normal text-amber-800">
            {DEMO_COMPANY_NAME} · read-only preview
          </span>
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={reset}>
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            Reset demo
          </Button>
          <Link to="/get-started">
            <Button size="sm" variant="outline">
              Get Started
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function DemoLayoutInner() {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <DemoBanner />
      <header className="border-b bg-navy-deep text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3">
          <Link to="/" className="flex items-center gap-2 font-semibold">
            <HardHat className="h-5 w-5 text-orange-construction" />
            {APP_NAME}
            <span className="rounded bg-white/10 px-2 py-0.5 text-xs font-medium">Demo</span>
          </Link>
          <nav className="hidden items-center gap-1 md:flex">
            {DEMO_NAV.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-orange-construction text-navy-deep"
                      : "text-white/80 hover:bg-white/10 hover:text-white",
                  )
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
          </nav>
          <Link to="/" className="text-sm text-white/70 hover:text-white">
            Back to site
          </Link>
        </div>
        <nav className="flex gap-1 overflow-x-auto border-t border-white/10 px-4 py-2 md:hidden">
          {DEMO_NAV.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  "shrink-0 rounded-md px-3 py-1.5 text-xs font-semibold",
                  isActive ? "bg-orange-construction text-navy-deep" : "bg-white/10 text-white",
                )
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto w-full max-w-7xl flex-1 p-4 md:p-6">
        <Outlet />
      </main>
    </div>
  );
}

export function DemoShell() {
  return (
    <DemoProvider>
      <DemoLayoutInner />
    </DemoProvider>
  );
}
